import { DATA_CONSUMED_KEY, DATA_GENERATED_KEY } from 'src/util/constants';
import logger from 'src/util/logger';
import {
  getAggregationDuration, getIntervalStart, hasTimeRangeOverlap, hasTimestampRangeOverlap,
  isValidTimeRange,
} from 'src/util/time/timeRange';

import { getPortfolioDataInBatches } from './dataBatching';

// ── Constants ─────────────────────────────────────────────────────
export const MAX_CACHE_SEGMENTS = 5;

/**
 * Build a composite cache key from historian and trade aggregation levels.
 * Segments cached at one trade granularity must not be served when
 * tradeAggregation differs, even if historianAggregation matches.
 * @param {string} historianAggregation - Aggregation for energy data.
 * @param {string} tradeAggregation - Aggregation for trade data.
 * @param {string} timezone - Timezone string (affects P1D bucket boundaries).
 * @returns {string} Composite key for cache segment matching.
 */
export const buildCacheAggregationKey = (historianAggregation, tradeAggregation, timezone) => (
  `${historianAggregation}|${tradeAggregation}|${timezone}`
);

// ── Module-level singleton cache ──────────────────────────────────
/**
 * Build a fresh cache object for a portfolio.
 * @param {string} portfolioId - Portfolio UUID to scope the cache.
 * @returns {object} Fresh cache object.
 */
export const buildInitialCache = (portfolioId) => ({
  portfolioId,
  segments: {},
});

let cache = buildInitialCache(null);

// ── Segment Lookup ────────────────────────────────────────────────
/**
 * Find a cached segment whose range fully covers the requested timeRange
 * and matches the aggregation level.
 * @param {object} segments - Cache segments keyed by `"startSec_finishSec"`.
 * @param {object} timeRange - Requested range with `start` and `finish` Luxon DateTimes.
 * @param {string} aggregation - Required aggregation level (e.g. 'PT30M', 'P1D').
 * @returns {object|null} Matching segment or null.
 */
export const getSegmentDataFromCache = (segments, timeRange, aggregation) => {
  if (!segments || !timeRange?.start || !timeRange?.finish) return null;
  const requestedStart = Math.floor(timeRange.start.toSeconds());
  const requestedFinish = Math.floor(timeRange.finish.toSeconds());

  const keys = Object.keys(segments);
  for (let i = 0; i < keys.length; i += 1) {
    const segment = segments[keys[i]];
    if (segment.aggregation === aggregation) {
      const segStart = Math.floor(segment.start.toSeconds());
      const segFinish = Math.floor(segment.finish.toSeconds());
      if (segStart <= requestedStart && segFinish >= requestedFinish) {
        return segment;
      }
    }
  }
  return null;
};

// ── Extract (filter to requested range) ───────────────────────────
/**
 * Extract a subset of combinedData filtered to the requested timeRange.
 *
 * Every filtered entity is normalised to a `{ start, finish }` range and retained when that
 * range overlaps the requested range. A measurement bucket is kept when any part of it falls
 * inside the view; a rule is kept when it is in force for any part of the view. The
 * boundaries are half-open `[start, finish)` — the range `dateRangeToTimeRange`
 * (`src/util/time.js`) produces and that `TradeRule.start`/`.finish` follow — so a range
 * finishing exactly at the requested start has already elapsed and is dropped, and a range
 * starting exactly at the requested finish has not begun and is dropped.
 *
 * Normalisation per entity, for each meter node:
 * - `dataConsumed.data` / `dataGenerated.data` — `{ start: getIntervalStart(timestamp,
 *   aggregation, timezone), finish: timestamp }`, where `aggregation` comes from the
 *   container; `timestamp` is the trailing edge of the interval the point covers
 * - `tradeSetSummary[].data` — `range` as supplied, both boundaries used
 * - `rules.edges` — each rule's own `start`/`finish`; `null` means unbounded (see
 *   `filterRules` below)
 *
 * On a measurement a `null` boundary means malformed, not unbounded, so measurements are
 * screened by `isValidTimeRange` before the overlap predicate runs; otherwise a measurement
 * with a missing boundary would be read as spanning all time and retained in every range.
 *
 * Portfolio-level `tradeSetSummary` is filtered the same way as per-meter.
 * All other fields (meter metadata, portfolio info) are copied as-is.
 * @param {object} combinedData - Cached combinedData from a segment.
 * @param {object} timeRange - Requested range with `start` and `finish` Luxon DateTimes.
 * @param {string} timezone - IANA zone the data was aggregated in, required to size a `P1D`
 *   bucket (see `getIntervalStart`). `getPortfolioDataFromCache` passes the request's zone,
 *   which equals the segment's because the cache key embeds it.
 * @returns {object} New combinedData scoped to the requested range.
 */
export const extractDataFromCache = (combinedData, timeRange, timezone) => {
  if (!combinedData?.portfolio?.meters?.edges || !timeRange?.start || !timeRange?.finish) {
    return combinedData;
  }

  if (!timezone) {
    // A missing timezone means an upstream invariant broke. Returning the data unfiltered
    // keeps the chart rendering, but it reintroduces the over-reporting this function exists
    // to fix, so it must be visible.
    logger.warn('extractDataFromCache: missing timezone — returning combinedData unfiltered');
    return combinedData;
  }

  const requestedRange = {
    start: Math.floor(timeRange.start.toSeconds()),
    finish: Math.floor(timeRange.finish.toSeconds()),
  };

  const filterTimeSeries = (dataObj) => {
    if (!dataObj?.data) return dataObj;
    if (dataObj.data.length > 0 && !getAggregationDuration(dataObj.aggregation)) {
      // Every point is about to be dropped: `getIntervalStart` has no bucket width to derive
      // a start from, so `isValidTimeRange` rejects each point below as malformed.
      logger.warn(
        `extractDataFromCache: unrecognised aggregation "${dataObj.aggregation}" — dropping all ${dataObj.data.length} points`,
      );
    }
    return {
      ...dataObj,
      data: dataObj.data.filter((point) => {
        const range = {
          start: getIntervalStart(point?.timestamp, dataObj.aggregation, timezone),
          finish: point?.timestamp,
        };
        return isValidTimeRange(range) && hasTimeRangeOverlap(range, requestedRange);
      }),
    };
  };

  const filterTradeSummaryData = (dataArray) => {
    if (!Array.isArray(dataArray)) return dataArray;
    return dataArray.filter((point) => {
      const range = point?.range;
      return !!range && isValidTimeRange(range) && hasTimeRangeOverlap(range, requestedRange);
    });
  };

  const filterTradeSummary = (summaryArray) => {
    if (!Array.isArray(summaryArray)) return summaryArray;
    return summaryArray.map((trade) => {
      if (!Array.isArray(trade?.data)) return trade;
      return {
        ...trade,
        data: filterTradeSummaryData(trade.data),
      };
    });
  };

  const filterRules = (rulesObj) => {
    if (!rulesObj?.edges) return rulesObj;
    // Spreading `rulesObj` keeps sibling connection fields (e.g. `pageInfo`) as-is.
    // Cached connections are never re-paginated, so the cursors are not re-derived
    // from the retained edges.
    return {
      ...rulesObj,
      edges: rulesObj.edges.filter((ruleEdge) => {
        if (!ruleEdge?.node) return false;
        const { start: ruleStart, finish: ruleFinish } = ruleEdge.node;
        // A rule is kept when it is in force for any part of the requested range. A null
        // start is treated as beginning of time and a null finish as end of time, so an
        // ongoing or backdated rule is never excluded.
        //
        // Example: requested range = [1 Jul 00:00, 3 Jul 00:00).
        //   Rule A: start=1 Jul 00:00, finish=2 Jul 00:00 → kept; in effect for the first two days.
        //   Rule B: start=28 Jun 00:00, finish=1 Jul 00:00 → dropped; `TradeRule.finish` is
        //   exclusive, so it stopped applying at the instant the requested range begins.
        return hasTimestampRangeOverlap({ start: ruleStart, finish: ruleFinish }, requestedRange);
      }),
    };
  };

  const filteredEdges = combinedData.portfolio.meters.edges.map((edge) => {
    const node = edge?.node;
    if (!node) return edge;
    return {
      ...edge,
      node: {
        ...node,
        [DATA_CONSUMED_KEY]: filterTimeSeries(node[DATA_CONSUMED_KEY]),
        [DATA_GENERATED_KEY]: filterTimeSeries(node[DATA_GENERATED_KEY]),
        tradeSetSummary: filterTradeSummary(node.tradeSetSummary),
        rules: filterRules(node.rules),
      },
    };
  });

  // Filter portfolio-level tradeSetSummary
  const filteredTradeSummary = Array.isArray(combinedData.portfolio.tradeSetSummary)
    ? combinedData.portfolio.tradeSetSummary.map((trade) => ({
      ...trade,
      data: filterTradeSummaryData(trade.data),
    }))
    : combinedData.portfolio.tradeSetSummary;

  return {
    ...combinedData,
    portfolio: {
      ...combinedData.portfolio,
      meters: {
        ...combinedData.portfolio.meters,
        edges: filteredEdges,
      },
      tradeSetSummary: filteredTradeSummary,
    },
  };
};

// ── Merge Helpers ─────────────────────────────────────────────────
/**
 * Build a composite key for trade deduplication.
 * @param {object} tradeKey - Trade key with tradePointId, ruleId, direction, type.
 * @returns {string} Composite key string.
 */
export const buildTradeKey = (tradeKey) => {
  if (!tradeKey) return '';
  const {
    tradePointId, ruleId, direction, type,
  } = tradeKey;
  return `${tradePointId || ''}_${ruleId || ''}_${direction || ''}_${type || ''}`;
};

/**
 * Merge two time-series arrays, deduplicating by timestamp.
 * Newer entries (from latestData) win on conflict.
 * @param {Array} existingData - Existing time-series data points.
 * @param {Array} latestData - Newer time-series data points to merge.
 * @returns {Array} Merged, deduplicated data sorted by timestamp.
 */
export const mergeUniqueTimeSeries = (existingData, latestData) => {
  const existing = Array.isArray(existingData) ? existingData : [];
  const latest = Array.isArray(latestData) ? latestData : [];
  if (existing.length === 0) return latest;
  if (latest.length === 0) return existing;

  const byTimestamp = new Map();
  existing.forEach((point) => { byTimestamp.set(point.timestamp, point); });
  latest.forEach((point) => { byTimestamp.set(point.timestamp, point); }); // newer wins
  return Array.from(byTimestamp.values()).sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Merge two trade summary data arrays, deduplicating by range.finish.
 * @param {Array} existingData - Existing trade summary data points.
 * @param {Array} latestData - Newer trade summary data points to merge.
 * @returns {Array} Merged, deduplicated data.
 */
export const mergeUniqueTradeSummaryData = (existingData, latestData) => {
  const existing = Array.isArray(existingData) ? existingData : [];
  const latest = Array.isArray(latestData) ? latestData : [];
  if (existing.length === 0) return latest;
  if (latest.length === 0) return existing;

  const byFinish = new Map();
  existing.forEach((point) => {
    if (point?.range?.finish) byFinish.set(point.range.finish, point);
  });
  latest.forEach((point) => {
    if (point?.range?.finish) byFinish.set(point.range.finish, point);
  });
  return Array.from(byFinish.values())
    .sort((a, b) => (a?.range?.finish ?? 0) - (b?.range?.finish ?? 0));
};

/**
 * Merge two combinedData objects (portfolio shape).
 * Merges per-meter consumed, generated, rules, and tradeSetSummary.
 * @param {object} existingData - Existing combinedData.
 * @param {object} latestData - Newer combinedData to merge in.
 * @returns {object} New combinedData with merged data.
 */
export const mergeCombinedData = (existingData, latestData) => {
  const existingEdges = existingData?.portfolio?.meters?.edges;
  if (!existingEdges) return latestData;

  const latestEdges = latestData?.portfolio?.meters?.edges;
  if (!latestEdges) return existingData;

  const latestByUuid = new Map();
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid) latestByUuid.set(edge.node.uuid, edge.node);
  });

  const matchedUuids = new Set();

  const mergedEdges = existingEdges.map((edge) => {
    const existingNode = edge?.node;
    if (!existingNode?.uuid) return edge;

    const latestNode = latestByUuid.get(existingNode.uuid);
    if (!latestNode) return edge;

    matchedUuids.add(existingNode.uuid);

    const mergedConsumed = {
      ...(existingNode[DATA_CONSUMED_KEY] || {}),
      ...(latestNode[DATA_CONSUMED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_CONSUMED_KEY]?.data,
        latestNode[DATA_CONSUMED_KEY]?.data,
      ),
    };

    const mergedGenerated = {
      ...(existingNode[DATA_GENERATED_KEY] || {}),
      ...(latestNode[DATA_GENERATED_KEY] || {}),
      data: mergeUniqueTimeSeries(
        existingNode[DATA_GENERATED_KEY]?.data,
        latestNode[DATA_GENERATED_KEY]?.data,
      ),
    };

    // Merge per-meter tradeSetSummary
    // Use an index map to avoid O(n) indexOf lookups per trade key.
    const mergedTrades = [...(existingNode.tradeSetSummary || [])];
    const tradeIndexMap = new Map();
    mergedTrades.forEach((trade, idx) => {
      tradeIndexMap.set(buildTradeKey(trade.key), idx);
    });

    (latestNode.tradeSetSummary || []).forEach((trade) => {
      const key = buildTradeKey(trade.key);
      const existingIdx = tradeIndexMap.get(key);
      if (existingIdx !== undefined) {
        mergedTrades[existingIdx] = {
          ...mergedTrades[existingIdx],
          data: mergeUniqueTradeSummaryData(mergedTrades[existingIdx].data, trade.data),
        };
      } else {
        tradeIndexMap.set(key, mergedTrades.length);
        mergedTrades.push(trade);
      }
    });

    // Merge rules (dedup by uuid)
    const mergedRulesEdges = [...(existingNode.rules?.edges || [])];
    const existingRuleIds = new Set(
      mergedRulesEdges.map((e) => e?.node?.uuid).filter(Boolean),
    );
    (latestNode.rules?.edges || []).forEach((ruleEdge) => {
      if (ruleEdge?.node?.uuid && !existingRuleIds.has(ruleEdge.node.uuid)) {
        mergedRulesEdges.push(ruleEdge);
      }
    });

    return {
      ...edge,
      node: {
        ...existingNode,
        ...latestNode,
        [DATA_CONSUMED_KEY]: mergedConsumed,
        [DATA_GENERATED_KEY]: mergedGenerated,
        tradeSetSummary: mergedTrades,
        rules: { edges: mergedRulesEdges },
      },
    };
  });

  // Append meters that exist only in latestData
  latestEdges.forEach((edge) => {
    if (edge?.node?.uuid && !matchedUuids.has(edge.node.uuid)) {
      mergedEdges.push(edge);
    }
  });

  // Merge portfolio-level tradeSetSummary
  // Use an index map to avoid O(n) indexOf lookups per trade key.
  const mergedPortfolioTrades = [...(existingData.portfolio?.tradeSetSummary || [])];
  const portfolioTradeIndexMap = new Map();
  mergedPortfolioTrades.forEach((trade, idx) => {
    portfolioTradeIndexMap.set(buildTradeKey(trade.key), idx);
  });

  (latestData.portfolio?.tradeSetSummary || []).forEach((trade) => {
    const key = buildTradeKey(trade.key);
    const existingIdx = portfolioTradeIndexMap.get(key);
    if (existingIdx !== undefined) {
      mergedPortfolioTrades[existingIdx] = {
        ...mergedPortfolioTrades[existingIdx],
        data: mergeUniqueTradeSummaryData(mergedPortfolioTrades[existingIdx].data, trade.data),
      };
    } else {
      portfolioTradeIndexMap.set(key, mergedPortfolioTrades.length);
      mergedPortfolioTrades.push(trade);
    }
  });

  return {
    ...existingData,
    portfolio: {
      ...existingData.portfolio,
      ...latestData.portfolio,
      meters: {
        ...existingData.portfolio.meters,
        edges: mergedEdges,
      },
      tradeSetSummary: mergedPortfolioTrades,
    },
  };
};

// ── Metadata helpers ──────────────────────────────────────────────
/**
 * Compute loading metadata from cached combinedData for overlay display.
 * @param {object} combinedData - Cached combinedData object.
 * @returns {object} { metersCount, rulesCount }
 */
export const computeCacheMetadata = (combinedData) => {
  const meterNodes = (combinedData?.portfolio?.meters?.edges || [])
    .map((edge) => edge?.node)
    .filter(Boolean);
  const rulesCount = meterNodes.reduce(
    (total, node) => total + (node.rules?.edges?.length || 0),
    0,
  );
  return { metersCount: meterNodes.length, rulesCount };
};

// ── Segment Management ────────────────────────────────────────────
/**
 * Add a new segment to the cache, merging with contiguous (overlapping or exactly touching)
 * segments if possible.
 * Evicts the oldest segment when MAX_CACHE_SEGMENTS is exceeded.
 * @param {object} timeRange - Range with `start` and `finish` Luxon DateTimes.
 * @param {object} combinedData - The merged data for this date range.
 * @param {string} aggregation - Aggregation level for this segment.
 */
export const addSegmentToCache = (timeRange, combinedData, aggregation) => {
  if (!timeRange?.start || !timeRange?.finish) return;

  let mergedData = combinedData;
  let mergedStart = timeRange.start;
  let mergedFinish = timeRange.finish;

  // Chain-merge: after absorbing one contiguous segment the expanded range
  // may now touch another, so restart the scan until no more merges occur.
  let merged = true;
  while (merged) {
    merged = false;
    const keys = Object.keys(cache.segments);
    for (let i = 0; i < keys.length; i += 1) {
      const seg = cache.segments[keys[i]];
      if (seg.aggregation !== aggregation) {
        // Different aggregation — skip
      } else {
        const segStartSec = Math.floor(seg.start.toSeconds());
        const segFinishSec = Math.floor(seg.finish.toSeconds());
        const curStartSec = Math.floor(mergedStart.toSeconds());
        const curFinishSec = Math.floor(mergedFinish.toSeconds());

        // Ranges are half-open `[start, finish)`, so segments are contiguous when they
        // overlap or touch exactly (one's finish equals the other's start). Any larger gap
        // is uncached time and must not be merged over.
        const isContiguous = segStartSec <= curFinishSec && curStartSec <= segFinishSec;

        if (isContiguous) {
          mergedData = mergeCombinedData(seg.data, mergedData);
          mergedStart = seg.start.toSeconds() < mergedStart.toSeconds()
            ? seg.start : mergedStart;
          mergedFinish = seg.finish.toSeconds() > mergedFinish.toSeconds()
            ? seg.finish : mergedFinish;
          delete cache.segments[keys[i]];
          merged = true;
          break; // restart scan after mutation
        }
      }
    }
  }

  const mergedStartSec = Math.floor(mergedStart.toSeconds());
  const mergedFinishSec = Math.floor(mergedFinish.toSeconds());
  const mergedKey = `${mergedStartSec}_${mergedFinishSec}`;

  cache.segments[mergedKey] = {
    start: mergedStart,
    finish: mergedFinish,
    data: mergedData,
    aggregation,
    createdAt: Date.now(),
  };

  // FIFO eviction
  const segKeys = Object.keys(cache.segments);
  if (segKeys.length > MAX_CACHE_SEGMENTS) {
    const oldest = segKeys.reduce((a, b) => (
      cache.segments[a].createdAt < cache.segments[b].createdAt ? a : b
    ));
    delete cache.segments[oldest];
  }
};

/**
 * Look up the cache for the current portfolioId. Returns the cache if
 * it matches; otherwise reinitialises for the new portfolio.
 * @param {string} portfolioId - Portfolio UUID.
 * @returns {object} The cache object for this portfolio.
 */
export const lookupCache = (portfolioId) => {
  if (cache.portfolioId !== portfolioId) {
    cache = buildInitialCache(portfolioId);
  }
  return cache;
};

/**
 * Clear the cache (for unmount or portfolio change).
 */
export const clearCache = () => {
  cache = buildInitialCache(null);
};

// ── Read-through cache layer ──────────────────────────────────────
/**
 * Attempt to serve the requested timeRange from cache. On cache hit,
 * returns the extracted data immediately. On cache miss, calls
 * getPortfolioDataInBatches (full 5-stage pipeline including meters)
 * and caches the result — matching Property Show's pattern.
 * @param {object} args
 * @param {string} args.portfolioId - Portfolio UUID
 * @param {object} args.timeRange - { start, finish } Luxon DateTimes
 * @param {string} args.timezone - Timezone string
 * @param {object} args.environment - Relay environment
 * @param {string} args.historianAggregation - Aggregation for energy data
 * @param {string} args.tradeAggregation - Aggregation for trade data
 * @param {object} args.initialData - Initial portfolio data
 * @param {Function} [args.dispatchFunc] - Reducer dispatch
 * @param {object}   [args.cancelledRef] - Cancellation token
 * @param {Function} [args.onDataReceivedFunc] - Called with
 *   (data, fromCache) on both hit and miss.
 * @param {Function} [args.onBeforeFetchFunc] - Called on cache miss
 *   before the fetch starts. Receives ({ segmentsExist }).
 * @param {Function} [args.onErrorFunc] - Error callback
 * @param {object}   [args.isFetchingRef] - Fetch guard ref
 */
export const getPortfolioDataFromCache = async (args) => {
  const {
    portfolioId, timeRange, timezone, environment,
    historianAggregation, tradeAggregation, initialData,
    dispatchFunc, cancelledRef, onDataReceivedFunc, onBeforeFetchFunc,
    onErrorFunc, isFetchingRef,
  } = args;

  const currentCache = lookupCache(portfolioId);
  const cacheKey = buildCacheAggregationKey(historianAggregation, tradeAggregation, timezone);
  const segment = getSegmentDataFromCache(currentCache.segments, timeRange, cacheKey);

  if (segment) {
    const extracted = extractDataFromCache(segment.data, timeRange, timezone);
    // Counts are taken from the extracted data, not the segment, so the log reflects
    // what is handed to the caller rather than the wider cached range.
    const { metersCount, rulesCount } = computeCacheMetadata(extracted);
    logger.log(`Cache HIT — serving from cache (meters=${metersCount}, rules=${rulesCount})`);

    if (onDataReceivedFunc) {
      onDataReceivedFunc(extracted, true);
    }
    return;
  }

  logger.log('Cache MISS — fetching from network');

  const segmentsExist = Object.keys(currentCache.segments).length > 0;

  if (onBeforeFetchFunc) {
    onBeforeFetchFunc({ segmentsExist });
  }

  await getPortfolioDataInBatches({
    portfolioId,
    timeRange,
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    initialData,
    dispatchFunc,
    cancelledRef,
    isFetchingRef,
    onDataReceived: (mergedData) => {
      // Cache the fetched data
      addSegmentToCache(timeRange, mergedData, cacheKey);
      if (onDataReceivedFunc) {
        onDataReceivedFunc(mergedData, false);
      }
    },
    onError: onErrorFunc,
  });
};
